import { useEffect, useRef, useState, type ReactNode } from 'react';
import type { PatternBased, RuleAction, RuleBased } from '@kkp-fraud/shared';
import { ApiError, api, type ApiSettings } from '../api';
import { branchesOf, patternBranchesOf } from '../branches';
import { ACTIONS } from '../fields';
import { formatBranches } from '../format';
import { t } from '../i18n';
import { DEMO } from '../demo';
import { contentHash, fromSchemaRefusal, fromValidation, problemsOfRule, type CheckResult, type CheckedProblem } from '../ruleCheck';
import { ProblemList } from './RuleCheckView';
import { newPatternBased, newRuleBased, upsertRuleIn, upsertedIndex, type RuleKind, type RuleSetDraft } from '../hooks/useRuleSetDraft';
import { ruleChanged } from '../scopedPublish';
import { ActionMenu } from './ActionMenu';
import { ActionConditionList } from './ActionConditionList';
import { PriorityChip } from './Chips';
import { RuleEditor, type EditorTarget } from './RuleEditor';
import { RuleHistoryDialog } from './RuleHistoryDialog';

/** Every action a rule can take, in row order: one per action row, or the rule's own. */
function actionsOf(rule: RuleBased | PatternBased): RuleAction[] {
  return ('clauses' in rule ? branchesOf(rule) : patternBranchesOf(rule)).map((row) => row.action);
}

/**
 * Tab 1 — every rule on one screen. A fraud analyst has to be able to answer "is this
 * rule on, and what does it actually do?" without opening anything.
 */

function RuleTable({
  title,
  kind,
  rules,
  onToggle,
  onOpen,
  controls,
  menu,
  readOnly = false,
  filtered = false,
}: {
  title: string;
  kind: 'ruleBased' | 'patternBased';
  rules: (RuleBased | PatternBased)[];
  onToggle: (kind: 'ruleBased' | 'patternBased', id: string) => void;
  onOpen: (kind: 'ruleBased' | 'patternBased', rule: RuleBased | PatternBased) => void;
  /** Search and filter, on the right of the heading — the first table on the screen only. */
  controls?: ReactNode;
  /** The row's "•••" menu. */
  menu: (kind: 'ruleBased' | 'patternBased', rule: RuleBased | PatternBased) => ReactNode;
  /** A Viewer: the switches still show on/off, but do not switch. */
  readOnly?: boolean;
  /** The family has rules and the search / action filter hides some — an empty table then means "no match". */
  filtered?: boolean;
}) {
  return (
    <section className="card">
      <header className="card-head">
        <h2>{title}</h2>
        <span className="spacer" />
        {controls}
      </header>
      <div className="tbl-wrap">
        <table className="rule-table">
          <thead>
            <tr>
              <th style={{ width: 68 }}>{t('rules.col.id')}</th>
              <th>{t('col.name.label')}</th>
              <th style={{ width: 52 }}>{t('rules.col.pri')}</th>
              <th>{t('col.actionCondition.label')}</th>
              <th style={{ width: 62 }}>{t('state.enabled.label')}</th>
              <th style={{ width: 40 }} />
            </tr>
          </thead>
          <tbody>
            {rules.length === 0 && (
              <tr>
                <td colSpan={6} className="empty">
                  {/* The family has rules and the search / action filter hid them all: say that (QA-R-11). */}
                  {filtered ? t('rules.noMatch.label') : t('rules.empty.label')}
                </td>
              </tr>
            )}
            {rules.map((rule) => (
              <tr
                key={rule.id}
                className={`clickable${rule.enabled ? '' : ' disabled'}`}
                onClick={() => onOpen(kind, rule)}
                data-testid={`rule-row-${rule.id}`}
              >
                <td className="mono nowrap">{rule.id}</td>
                <td>
                  {/* The row still opens on a click anywhere; the name is its keyboard way in (UX review P1-5). */}
                  <button
                    type="button"
                    className="rowlink"
                    data-testid={`rule-open-${rule.id}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      onOpen(kind, rule);
                    }}
                  >
                    {rule.name || rule.id}
                  </button>
                  {rule.rationale?.trim() && (
                    <div className="small muted clamp-2" title={rule.rationale} data-testid={`rule-rationale-${rule.id}`}>
                      {rule.rationale}
                    </div>
                  )}
                </td>
                <td>
                  <PriorityChip priority={rule.priority} />
                </td>
                <td style={{ maxWidth: 560 }}>
                  <ActionConditionList rule={rule} testid={`rule-actions-${rule.id}`} />
                </td>
                <td onClick={(e) => e.stopPropagation()}>
                  <input
                    type="checkbox"
                    role="switch"
                    className="toggle"
                    checked={rule.enabled}
                    disabled={readOnly}
                    aria-label={t('rules.toggle.a11y', { id: rule.id })}
                    onChange={() => onToggle(kind, rule.id)}
                  />
                </td>
                {/* The menu and its items must not open the editor behind them. */}
                <td className="row-menu-cell" onClick={(e) => e.stopPropagation()}>
                  {menu(kind, rule)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}


/**
 * `only` narrows the screen to one rule family. Rule Management (Transaction) shows RB and
 * Rule Management (Pattern base) shows PB, but both edit the SAME document — the set-level
 * settings (`RuleSetSettings`, behind the version line's "…") are shared, and so is the
 * version that gets released. Omitting `only` keeps the original both-tables view.
 */
export function RulesTab({
  state,
  settings,
  only,
  onPublish,
  onToast,
  addRequest,
  onAddHandled,
  onAdd,
  openRequest,
  onOpenHandled,
  readOnly = false,
}: {
  state: RuleSetDraft;
  /** Where the editor's dry run and the row history read from. */
  settings: ApiSettings;
  only?: 'ruleBased' | 'patternBased';
  /** A row's "Publish": open the release dialog scoped to that rule. Absent → the item is disabled. */
  onPublish?: (kind: RuleKind, id: string) => void;
  onToast?: (message: string, kind?: 'ok' | 'error') => void;
  /** The screen's "+ Add rule" was pressed: open the new-rule editor of this family, then say so. */
  addRequest?: RuleKind | null;
  onAddHandled?: () => void;
  /**
   * "+ Add rule", drawn at the right of the first table's head (UX review P2-10). Absent → no
   * button (a Viewer).
   */
  onAdd?: () => void;
  /** A link asked for this rule (`?rule=<id>`): open its editor once, then say so. */
  openRequest?: string | null;
  onOpenHandled?: (found: boolean) => void;
  /**
   * A Viewer: every rule can be read — opened, its history seen, exported — and none changed.
   * No switching, no Replace, no Restore, and the editor opens read-only.
   */
  readOnly?: boolean;
}) {
  const [target, setTarget] = useState<EditorTarget | null>(null);
  const [history, setHistory] = useState<{ kind: RuleKind; id: string } | null>(null);
  /** Why the last Replace was refused — shown until the next attempt or dismissed. */
  const [replaceIssue, setReplaceIssue] = useState<{ id: string; message: string; problems: CheckedProblem[] } | null>(null);
  const fileInput = useRef<HTMLInputElement>(null);
  const replaceTarget = useRef<{ kind: RuleKind; id: string } | null>(null);
  const [query, setQuery] = useState('');
  const [actionFilter, setActionFilter] = useState<string>('all');

  const draft = state.draft;

  /*
   * The request is a pending flag the screen clears through `onAddHandled`, not a counter:
   * this tab unmounts on Dry-run, and a counter would re-open the editor on every remount.
   */
  useEffect(() => {
    if (!addRequest || !draft || readOnly) return;
    const ids = [...draft.ruleBased, ...draft.patternBased].map((r) => r.id);
    setTarget(
      addRequest === 'ruleBased'
        ? { kind: 'ruleBased', rule: newRuleBased(ids), isNew: true }
        : { kind: 'patternBased', rule: newPatternBased(ids), isNew: true },
    );
    onAddHandled?.();
    // Only a new request opens the editor; a draft edit while it is pending does not.
  }, [addRequest]);

  /* A rule link (Dashboard, Report, Log…): open that rule, read-only for a Viewer as ever. */
  useEffect(() => {
    if (!openRequest || !draft) return;
    const rb = only !== 'patternBased' ? draft.ruleBased.find((r) => r.id === openRequest) : undefined;
    const pb = only !== 'ruleBased' ? draft.patternBased.find((r) => r.id === openRequest) : undefined;
    if (rb) setTarget({ kind: 'ruleBased', rule: rb, isNew: false });
    else if (pb) setTarget({ kind: 'patternBased', rule: pb, isNew: false });
    onOpenHandled?.(Boolean(rb ?? pb));
    // Only a new request opens the editor.
  }, [openRequest]);

  const filter = <T extends RuleBased | PatternBased>(rules: T[]): T[] =>
    rules.filter((r) => {
      // A rule matches an action when ANY of its action rows takes it.
      if (actionFilter !== 'all' && !actionsOf(r).includes(actionFilter as RuleAction)) return false;
      if (!query.trim()) return true;
      const q = query.toLowerCase();
      return (
        r.id.toLowerCase().includes(q) ||
        r.name.toLowerCase().includes(q) ||
        formatBranches(r).some((line) => line.condition.toLowerCase().includes(q))
      );
    });

  if (!draft) return <div className="empty">{t('rules.noDocument.label')}</div>;

  /** A search or an action filter is narrowing the tables. */
  const searching = actionFilter !== 'all' || query.trim() !== '';

  const openEditor = (kind: 'ruleBased' | 'patternBased', rule: RuleBased | PatternBased) =>
    setTarget({ kind, rule, isNew: false } as EditorTarget);

  const toast = (message: string, kind: 'ok' | 'error' = 'ok') => onToast?.(message, kind);

  /**
   * Replace one rule's definition from a JSON file — a bare rule or `{ "rule": … }` — into the
   * draft only. The row decides the family and the id; a file that is not a rule of this
   * family, or one the engine's check finds problems in, leaves the draft untouched. When the
   * engine cannot be asked (no server, demo mode) the rule goes into the draft unchecked — the
   * release is still gated on the engine's answer.
   */
  const replaceFromFile = async (file: File, kind: RuleKind, id: string) => {
    const refuse = (message: string, problems: CheckedProblem[] = []) => {
      setReplaceIssue({ id, message, problems });
      toast(message, 'error');
    };
    let parsed: unknown;
    try {
      parsed = JSON.parse(await file.text());
    } catch {
      return refuse(t('row.replace.invalidJson', { id }));
    }
    const isObject = (v: unknown): v is Record<string, unknown> => typeof v === 'object' && v !== null && !Array.isArray(v);
    const body = isObject(parsed) && isObject(parsed.rule) ? parsed.rule : parsed;
    if (!isObject(body)) return refuse(t('row.replace.invalidJson', { id }));

    const looksRb = Array.isArray(body.clauses);
    const looksPb = 'scope' in body || 'windowSeconds' in body;
    const fileKind: RuleKind | null = looksRb && !looksPb ? 'ruleBased' : looksPb && !looksRb ? 'patternBased' : null;
    if (fileKind !== kind) return refuse(t('row.replace.kindMismatch', { id }));

    const from = typeof body.id === 'string' ? body.id : '';
    const rule = { ...body, id } as unknown as RuleBased | PatternBased;
    const problems = await replaceProblems(kind, rule, id);
    if (problems.length > 0) {
      return refuse(problems.length === 1 ? t('draft.problems.one', { n: 1 }) : t('draft.problems.many', { n: problems.length }), problems);
    }

    setReplaceIssue(null);
    state.upsertRule(kind, rule);
    toast(from && from !== id ? t('row.replace.idChanged', { id, from }) : t('row.replace.done', { id }));
  };

  /**
   * The engine's problems with `rule` in place of `id` (only that rule's). `[]` when it finds
   * none — and when it cannot be asked: a check that did not happen is not a refusal.
   */
  const replaceProblems = async (kind: RuleKind, rule: RuleBased | PatternBased, id: string): Promise<CheckedProblem[]> => {
    if (DEMO || readOnly || !draft) return [];
    const candidate = upsertRuleIn(draft, kind, rule, id);
    const hash = contentHash(candidate);
    let result: CheckResult;
    try {
      result = fromValidation(hash, await api.validate(settings, candidate));
    } catch (e) {
      if (!(e instanceof ApiError && e.status === 400 && e.problems?.length)) return [];
      result = fromSchemaRefusal(hash, e.problems);
    }
    return problemsOfRule(result.problems, kind, upsertedIndex(draft, kind, rule, id), id);
  };

  /** Download the rule as it is in the draft — the file Replace reads back unchanged. */
  const exportRule = (rule: RuleBased | PatternBased) => {
    const blob = new Blob([`${JSON.stringify(rule, null, 2)}\n`], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${rule.id}.json`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    // Revoked on the next tick: some browsers cancel a download whose URL is revoked synchronously.
    setTimeout(() => URL.revokeObjectURL(url), 0);
  };

  const rowMenu = (kind: RuleKind, rule: RuleBased | PatternBased) => (
    <ActionMenu
      variant="row"
      label={t('row.menu.a11y', { id: rule.id })}
      items={[
        // Everyday first, the release last and set apart (UX review P1-5): Edit → history →
        // export → the draft-changing file action → Publish.
        { label: readOnly ? t('row.view.label') : t('row.edit.label'), onSelect: () => openEditor(kind, rule) },
        { label: t('row.history.label'), onSelect: () => setHistory({ kind, id: rule.id }) },
        { label: t('row.export.label'), onSelect: () => exportRule(rule) },
        // It changes the draft, so a Viewer does not have it.
        ...(readOnly
          ? []
          : [
              {
                label: t('row.replace.label'),
                onSelect: () => {
                  replaceTarget.current = { kind, id: rule.id };
                  fileInput.current?.click();
                },
              },
            ]),
        // No `onPublish` = this account may not publish (a Viewer): no Publish item at all.
        // It keeps its confirm step: it opens the release dialog, which asks before publishing.
        ...(onPublish && !readOnly
          ? [
              {
                label: t('row.publish.label'),
                disabled: !ruleChanged(state.loaded, draft, kind, rule.id),
                onSelect: () => onPublish(kind, rule.id),
                separated: true,
              },
            ]
          : []),
      ]}
    />
  );

  /** Search and the action filter: one set for the screen, on the first table's heading. */
  const controls = (
    <>
      <input
        type="text"
        aria-label={t('rules.search.a11y')}
        placeholder={t('rules.search.placeholder')}
        style={{ width: 240 }}
        value={query}
        onChange={(e) => setQuery(e.target.value)}
      />
      <select aria-label={t('rules.actionFilter.a11y')} value={actionFilter} onChange={(e) => setActionFilter(e.target.value)} style={{ width: 150 }}>
        <option value="all">{t('rules.actionFilter.all')}</option>
        {ACTIONS.map((a) => (
          <option key={a} value={a}>
            {a}
          </option>
        ))}
      </select>
      {onAdd && !readOnly && (
        <button type="button" className="btn small" onClick={onAdd}>
          {t('btn.add_rule.label')}
        </button>
      )}
    </>
  );

  return (
    <>
      <input
        ref={fileInput}
        type="file"
        accept="application/json,.json"
        hidden
        data-testid="rule-replace-input"
        onChange={(e) => {
          const file = e.target.files?.[0];
          const at = replaceTarget.current;
          e.target.value = '';
          if (file && at) void replaceFromFile(file, at.kind, at.id);
        }}
      />

      {replaceIssue && (
        <div className="banner error" role="status" data-testid="rule-replace-problems">
          <div className="row-wrap">
            <strong>
              <span className="mono">{replaceIssue.id}</span> · {replaceIssue.message}
            </strong>
            <span className="spacer" />
            <button type="button" className="btn small" aria-label={t('btn.close.label')} onClick={() => setReplaceIssue(null)}>
              ✕
            </button>
          </div>
          {replaceIssue.problems.length > 0 && <ProblemList problems={replaceIssue.problems} limit={12} />}
        </div>
      )}

      {only !== 'patternBased' && (
        <RuleTable
          title={t('kind.rb.label')}
          kind="ruleBased"
          rules={filter(draft.ruleBased)}
          filtered={draft.ruleBased.length > 0 && searching}
          onToggle={state.toggleEnabled}
          onOpen={openEditor}
          controls={controls}
          menu={rowMenu}
          readOnly={readOnly}
        />
      )}
      {only !== 'ruleBased' && (
        <RuleTable
          title={t('kind.pb.label')}
          kind="patternBased"
          rules={filter(draft.patternBased)}
          filtered={draft.patternBased.length > 0 && searching}
          onToggle={state.toggleEnabled}
          onOpen={openEditor}
          controls={only === 'patternBased' ? controls : undefined}
          menu={rowMenu}
          readOnly={readOnly}
        />
      )}

      {target && (
        <RuleEditor
          target={target}
          onSave={(kind, rule, previousId) => {
            state.upsertRule(kind, rule, previousId);
            setTarget(null);
          }}
          onDelete={
            readOnly
              ? undefined
              : (kind, id) => {
                  state.removeRule(kind, id);
                  setTarget(null);
                }
          }
          onClose={() => setTarget(null)}
          settings={settings}
          ruleSet={draft}
          readOnly={readOnly}
        />
      )}

      {history && (
        <RuleHistoryDialog
          kind={history.kind}
          id={history.id}
          settings={settings}
          onClose={() => setHistory(null)}
          onRestore={
            readOnly
              ? undefined
              : (rule, version) => {
                  state.upsertRule(history.kind, rule);
                  setHistory(null);
                  toast(t('row.history.restored', { id: rule.id, version }));
                }
          }
        />
      )}
    </>
  );
}
